import { useMessages } from "next-intl";

/**
 * Translation hook using next-intl
 *
 * Available namespaces:
 * - common: Shared UI elements (buttons, labels, etc.)
 * - menu: Menu management
 * - orders: Order management
 * - settings: Settings pages
 * - analytics: Analytics and reporting
 * - onboarding: Onboarding flow
 * - regions: Location/region management
 * - notifications: Notification system
 * - integrations: Third-party integrations
 *
 * Usage:
 *   const { t } = useTStream("common");
 *   t("save")                          // → "Save"
 *   t("hello", { name: "World" })      // → "Hello World" (ICU format)
 *
 * Key naming convention: camelCase (e.g., "saveChanges", "allModifiers")
 */

// Valid namespace prefixes (matching colocated i18n folders)
type ValidPrefixes =
  | "common"
  | "menu"
  | "orders"
  | "settings"
  | "analytics"
  | "onboarding"
  | "regions"
  | "notifications"
  | "modifiers"
  | "catalog";

type TranslationBody = Record<string, unknown>;

type Messages = Record<string, Record<string, string>>;

export function useTStream(): {
  t: (key: string, body?: TranslationBody) => string;
};

export function useTStream(prefix: ValidPrefixes): {
  t: (key: string, body?: TranslationBody) => string;
};

export function useTStream(prefix: ValidPrefixes = "common") {
  const messages = useMessages() as Messages;
  const namespace = messages[prefix] || {};

  return {
    t: (key: string, body?: TranslationBody): string => {
      let value = namespace[key];

      // Graceful fallback if translation key is missing — return the key itself
      // instead of throwing, so the page stays functional.
      if (value === undefined) {
        const message =
          `Missing translation key: "${prefix}.${key}"\n` +
          `Add this key to: dictionaries/en/${prefix}.json`;

        if (process.env.NODE_ENV === "development") {
          console.error(message);
        } else {
          console.warn(message);
        }

        return key;
      }

      // Handle {{variable}} interpolation
      if (body && typeof value === "string") {
        value = Object.keys(body).reduce((acc, k) => {
          return acc.replace(
            new RegExp(`\\{\\{${k}\\}\\}`, "g"),
            String(body[k]),
          );
        }, value);
      }

      return value;
    },
  };
}
